<template>
  <Teleport to="body">
    <dialog
      ref="dialog"
      class="sponsor-required"
      aria-labelledby="sponsor-required-title"
      aria-describedby="sponsor-required-description"
      @cancel.prevent
    >
      <form class="sponsor-required__content" @submit.prevent="submit">
        <img class="sponsor-required__mascot" :src="iskra" alt="" width="160" height="160" />
        <p class="sponsor-required__eyebrow">{{ t('sponsorRequired.eyebrow') }}</p>
        <h2 id="sponsor-required-title" class="sponsor-required__title">{{ t('sponsorRequired.title') }}</h2>
        <p id="sponsor-required-description" class="sponsor-required__description">{{ t('sponsorRequired.description') }}</p>
        <BaseInput
          profile
          ref="input"
          v-model="login"
          :label="t('sponsorRequired.label')"
          :placeholder="t('sponsorRequired.placeholder')"
          :disabled="busy"
          autocomplete="off"
          autocapitalize="none"
          :spellcheck="false"
          maxlength="255"
          @update:model-value="error = ''"
        />
        <p v-if="error" class="sponsor-required__error" role="alert">{{ error }}</p>
        <BaseButton type="submit" monochrome size="lg" block :disabled="busy || !login.trim()" :loading="busy">
          {{ t('sponsorRequired.submit') }}
        </BaseButton>
        <p class="sponsor-required__hint">{{ t('sponsorRequired.hint') }}</p>
        <div class="sponsor-required__alternatives">
          <BaseButton type="button" monochrome variant="ghost" size="lg" block :disabled="busy" @click="emit('profile')">
            {{ t('sponsorRequired.profile') }}
          </BaseButton>
          <BaseButton type="button" monochrome variant="ghost" size="lg" block :disabled="busy" @click="logout">
            {{ t('sponsorRequired.logout') }}
          </BaseButton>
        </div>
      </form>
    </dialog>
  </Teleport>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import BaseButton from '@/shared/components/base/BaseButton.vue'
import BaseInput from '@/shared/components/base/BaseInput.vue'
import iskra from '@/modules/auth/assets/landing/iskra-thinking.webp'
import { useProfileStore } from '../store/profileStore'

const emit = defineEmits<{ profile: []; saved: [] }>()
const { t } = useI18n()
const store = useProfileStore()
const dialog = ref<HTMLDialogElement>()
const input = ref<InstanceType<typeof BaseInput>>()
const login = ref('')
const error = ref('')
const busy = ref(false)

onMounted(async () => {
  dialog.value?.showModal()
  await nextTick()
  input.value?.focus()
})
onBeforeUnmount(() => dialog.value?.close())

async function submit() {
  if (busy.value || !login.value.trim()) return
  busy.value = true
  error.value = ''
  try {
    if (await store.assignSponsor(login.value)) emit('saved')
    else error.value = t('sponsorRequired.unconfirmed')
  } catch (cause) {
    const data = cause as { response?: { data?: { error_message?: string } }; message?: string }
    const message = data?.response?.data?.error_message ?? data?.message ?? ''
    error.value = message.includes('SPONSOR_NOT_FOUND')
      ? t('sponsorRequired.notFound')
      : message.includes('SPONSOR_INVALID_TOPOLOGY')
        ? t('sponsorRequired.invalid')
        : t('sponsorRequired.unconfirmed')
  } finally {
    busy.value = false
  }
}

async function logout() {
  if (busy.value) return
  busy.value = true
  await store.logout()
}
</script>

<style scoped lang="scss">
.sponsor-required {
  width: min(480px, calc(100vw - 32px));
  max-height: calc(100dvh - 32px);
  padding: 0;
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-radius-modal);
  background: var(--ds-surface);
  color: var(--ds-text);
  box-shadow: 0 24px 80px var(--ds-scrim);

  &::backdrop {
    background: var(--ds-scrim);
    backdrop-filter: blur(8px);
  }

  &__content { display: grid; gap: 16px; padding: 28px; }
  &__mascot { display: block; width: 140px; height: 140px; object-fit: contain; margin: -8px auto 0; }
  &__eyebrow { margin: 0; text-align: center; color: var(--ds-text-muted); font-size: 12px; }
  &__title { margin: 0; text-align: center; font-size: clamp(24px, 5vw, 30px); line-height: 1.15; letter-spacing: -.03em; }
  &__description, &__hint { margin: 0; color: var(--ds-text-secondary); font-size: 14px; line-height: 1.6; }
  &__description { text-align: center; }
  &__hint { font-size: 12px; }
  &__error { margin: 0; color: var(--ds-danger); font-size: 13px; }
  &__alternatives { display: grid; gap: 8px; }

  @media (max-height: 700px) {
    &__mascot { width: 90px; height: 90px; }
    &__content { padding: 20px; gap: 12px; }
  }
}
</style>
